import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    Box, Typography, Paper, Grid, Container, Button,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Chip, TextField, Fade, Avatar, Divider,
    FormControl, InputLabel, Select, MenuItem, CircularProgress,
    IconButton, Tooltip, LinearProgress, InputAdornment,
    Tabs, Tab, Card, CardContent, CardActions,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import {
    Save as SaveIcon,
    Class as ClassIcon,
    Subject as SubjectIcon,
    Assignment as AssignmentIcon,
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckCircleIcon,
    BarChart as BarChartIcon,
    Search as SearchIcon,
    Info as InfoIcon,
    TrendingUp as TrendingUpIcon,
    People as PeopleIcon,
    Percent as PercentIcon,
    Warning as WarningIcon,
    History as HistoryIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    EventNote as EventNoteIcon,
    CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import { useToast } from '../../context/ToastContext';
import { CustomNepaliDatePicker, getCurrentBSDate, formatDatePart, getMonthNameFromDate } from '../../utils/customDate';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 60px 0 80px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -60px;
    position: relative;
    z-index: 2;
`;

const PremiumCard = styled(Paper)`
    border-radius: 24px !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.05) !important;
    overflow: hidden;
    margin-bottom: 30px;
`;

const StyledTableCell = styled(TableCell)`
    font-weight: 700 !important;
    color: #475569 !important;
    background-color: #f8fafc !important;
    border-bottom: 2px solid #e2e8f0 !important;
    text-transform: uppercase;
    font-size: 0.75rem !important;
    letter-spacing: 0.05em;
`;

const StatCard = styled(Paper)`
    padding: 20px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    background: white;
    border: 1px solid #eef2ff;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 24px -10px rgba(99, 102, 241, 0.15);
    }
`;

const SessionCard = styled(Card)`
    border-radius: 16px !important;
    border: 1px solid #e2e8f0 !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    height: 100%;
    display: flex;
    flex-direction: column;

    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
        border-color: #6366f1 !important;
    }
`;

const TeacherMarks = () => {
    const { currentUser } = useSelector((state) => state.user);
    const { showToast } = useToast();

    // Tab state
    const [tabValue, setTabValue] = useState(0);

    const [classes, setClasses] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [students, setStudents] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [existingMarks, setExistingMarks] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    // Selection states
    const [selectedClass, setSelectedClass] = useState('');
    const [selectedSubject, setSelectedSubject] = useState('');
    const [examType, setExamType] = useState('');
    const [examDate, setExamDate] = useState(getCurrentBSDate());
    const [maxMarks, setMaxMarks] = useState('100');
    const [passMarks, setPassMarks] = useState('40');

    // Marks entry state
    const [studentMarks, setStudentMarks] = useState({});

    // UI states
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    
    // Delete Dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [sessionToDelete, setSessionToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const teacherId = currentUser?._id;

    useEffect(() => {
        if (teacherId) {
            fetchTeacherClasses();
            fetchHistory();
        }
    }, [teacherId]);

    useEffect(() => {
        if (teacherId && selectedClass) fetchTeacherSubjects();
        else { setSubjects([]); setSelectedSubject(''); }
    }, [selectedClass, teacherId]);

    useEffect(() => {
        if (selectedClass && selectedSubject && examType && examDate) {
            fetchStudents();
            fetchExistingMarks();
        } else {
            setStudents([]);
            setExistingMarks({});
            setStudentMarks({});
        }
    }, [selectedClass, selectedSubject, examType, examDate]);

    const fetchTeacherClasses = async () => {
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/Teacher/Classes/${teacherId}`);
            if (Array.isArray(result.data)) setClasses(result.data);
        } catch (error) {
            console.error('Error fetching classes:', error);
        }
    };

    const fetchTeacherSubjects = async () => {
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/Teacher/Subjects/${teacherId}/${selectedClass}`);
            if (Array.isArray(result.data)) setSubjects(result.data);
        } catch (error) {
            console.error('Error fetching subjects:', error);
        }
    };

    const fetchStudents = async () => {
        setLoading(true);
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/Sclass/Students/${selectedClass}`);
            if (Array.isArray(result.data)) setStudents(result.data);
        } catch (error) {
            console.error('Error fetching students:', error);
        }
        setLoading(false);
    };

    const fetchHistory = async () => {
        setHistoryLoading(true);
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/Marks/Sessions/${teacherId}`);
            if (Array.isArray(result.data)) setSessions(result.data);
        } catch (error) {
            console.error('Error fetching history:', error);
        }
        setHistoryLoading(false);
    };

    const fetchExistingMarks = async () => {
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/Marks/Search`, {
                params: {
                    classId: selectedClass,
                    subId: selectedSubject,
                    examType: examType,
                    examDate: examDate
                }
            });
            
            if (Array.isArray(result.data)) {
                const marksMap = {};
                const entryMap = {};
                result.data.forEach(mark => {
                    marksMap[mark.student] = mark.marksObtained;
                    entryMap[mark.student] = mark.marksObtained;
                });
                setExistingMarks(marksMap);
                setStudentMarks(entryMap);
                if (result.data.length > 0) {
                    if (result.data[0].maxMarks) setMaxMarks(result.data[0].maxMarks.toString());
                    if (result.data[0].passMarks) setPassMarks(result.data[0].passMarks.toString());
                }
            }
        } catch (error) {
            console.error('Error fetching existing marks:', error);
        }
    };

    const handleMarksChange = (studentId, value) => {
        if (value === '' || (Number(value) >= 0 && Number(value) <= Number(maxMarks))) {
            setStudentMarks(prev => ({ ...prev, [studentId]: value }));
        } else {
            showToast(`Marks cannot exceed ${maxMarks}`, 'warning');
        }
    };

    const handleSaveMarks = async () => {
        if (!selectedClass || !selectedSubject || !examType) {
            showToast('Please complete all selections', 'warning');
            return;
        }

        setSaving(true);
        try {
            const marksData = Object.entries(studentMarks).map(([studentId, marks]) => ({
                studentID: studentId,
                teacherID: teacherId,
                schoolID: currentUser.school,
                sclassID: selectedClass,
                subjectID: selectedSubject,
                marksObtained: marks,
                maxMarks: maxMarks,
                passMarks: passMarks,
                examType: examType,
                examDate: examDate,
                comments: ''
            }));

            await axios.post(`${process.env.REACT_APP_BASE_URL}/Marks/BulkCreate`, { marksData });
            showToast('Marks saved successfully!', 'success');
            fetchExistingMarks();
            fetchHistory(); // Refresh history
        } catch (error) {
            console.error('Error saving marks:', error);
            showToast('Error saving marks', 'error');
        }
        setSaving(false);
    };

    const handleEditSession = (session) => {
        setSelectedClass(session.classId);
        setSelectedSubject(session.subjectId);
        setExamType(session.examType);
        setExamDate(formatDatePart(session.examDate));
        setMaxMarks(session.maxMarks?.toString() || '100');
        setPassMarks(session.passMarks?.toString() || '40');
        setTabValue(0); // Switch to record tab
        showToast(`Loaded ${session.class} - ${session.subject}`, 'info');
    };

    const handleDeleteClick = (session) => {
        setSessionToDelete(session);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!sessionToDelete) return;
        
        setDeleting(true);
        try {
            await axios.delete(`${process.env.REACT_APP_BASE_URL}/Marks/Session`, {
                params: {
                    teacherId: teacherId,
                    classId: sessionToDelete.classId,
                    subjectId: sessionToDelete.subjectId,
                    examType: sessionToDelete.examType,
                    examDate: sessionToDelete.examDate
                }
            });
            showToast('Session deleted successfully', 'success');
            fetchHistory();
            // If current view is the deleted session, clear it
            if (selectedClass === sessionToDelete.classId && 
                selectedSubject === sessionToDelete.subjectId && 
                examType === sessionToDelete.examType && 
                examDate === formatDatePart(sessionToDelete.examDate)) {
                setExistingMarks({});
                setStudentMarks({});
            }
        } catch (error) {
            console.error('Error deleting session:', error);
            showToast('Error deleting session', 'error');
        }
        setDeleting(false);
        setDeleteDialogOpen(false);
        setSessionToDelete(null);
    };

    const filteredStudents = useMemo(() => {
        if (!searchTerm) return students;
        return students.filter(s => 
            s.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
            s.rollNum?.toString().includes(searchTerm)
        );
    }, [students, searchTerm]);

    const stats = useMemo(() => {
        const total = students.length;
        const entered = Object.values(studentMarks).filter(v => v !== '' && v !== undefined).length;
        const marksValues = Object.values(studentMarks).filter(v => v !== '').map(Number);
        const average = entered > 0 
            ? (marksValues.reduce((a, b) => a + b, 0) / entered).toFixed(1)
            : 0;
        const passCount = marksValues.filter(v => v >= Number(passMarks)).length;
        const passRate = entered > 0 ? ((passCount / entered) * 100).toFixed(0) : 0;
        const progress = total > 0 ? (entered / total) * 100 : 0;
        
        return { total, entered, average, progress, passRate };
    }, [students, studentMarks, passMarks]);

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="lg">
                    <Fade in timeout={800}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <IconButton onClick={() => window.history.back()} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                                    <ArrowBackIcon />
                                </IconButton>
                                <Box>
                                    <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                                        Marks Management
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
                                        Record and review academic performance.
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Box sx={{ 
                                bgcolor: 'rgba(255,255,255,0.1)', 
                                p: 0.5, 
                                borderRadius: '16px', 
                                backdropFilter: 'blur(10px)',
                                zIndex: 10,
                                position: 'relative'
                            }}>
                                <Tabs 
                                    value={tabValue} 
                                    onChange={(e, v) => setTabValue(v)}
                                    sx={{ 
                                        minHeight: 'auto',
                                        '& .MuiTabs-indicator': { height: '100%', borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.2)', zIndex: 0 },
                                        '& .MuiTab-root': { minHeight: 'auto', py: 1.5, px: 3, color: 'white', fontWeight: 700, zIndex: 1, textTransform: 'none', borderRadius: '12px', '&.Mui-selected': { color: 'white' } }
                                    }}
                                >
                                    <Tab icon={<AssignmentIcon sx={{ mr: 1 }} />} iconPosition="start" label="Record Marks" />
                                    <Tab icon={<HistoryIcon sx={{ mr: 1 }} />} iconPosition="start" label="Marks History" />
                                </Tabs>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="lg" sx={{ mt: 2 }}>
                {tabValue === 0 ? (
                    <Fade in timeout={500}>
                        <Box>
                            <PremiumCard sx={{ p: 4, mt: 2}}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={3}>
                                        <FormControl fullWidth>
                                            <InputLabel>Class</InputLabel>
                                            <Select
                                                value={selectedClass}
                                                label="Class"
                                                onChange={(e) => { setSelectedClass(e.target.value); setSelectedSubject(''); }}
                                                sx={{ borderRadius: '12px' }}
                                            >
                                                {classes.map((cls) => (
                                                    <MenuItem key={cls._id} value={cls._id}>{cls.sclassName}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <FormControl fullWidth disabled={!selectedClass}>
                                            <InputLabel>Subject</InputLabel>
                                            <Select
                                                value={selectedSubject}
                                                label="Subject"
                                                onChange={(e) => setSelectedSubject(e.target.value)}
                                                sx={{ borderRadius: '12px' }}
                                            >
                                                {subjects.map((sub) => (
                                                    <MenuItem key={sub._id} value={sub._id}>{sub.subName}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <FormControl fullWidth>
                                            <InputLabel>Exam Type</InputLabel>
                                            <Select
                                                value={examType}
                                                label="Exam Type"
                                                onChange={(e) => setExamType(e.target.value)}
                                                sx={{ borderRadius: '12px' }}
                                            >
                                                <MenuItem value="First Terminal">First Terminal</MenuItem>
                                                <MenuItem value="Second Terminal">Second Terminal</MenuItem>
                                                <MenuItem value="Mid-Terminal">Mid-Terminal</MenuItem>
                                                <MenuItem value="Annual">Annual Exam</MenuItem>
                                                <MenuItem value="Test">Test / Pre-Board</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <CustomNepaliDatePicker
                                            label="Exam Date"
                                            value={examDate}
                                            onChange={(date) => setExamDate(date)}
                                        />
                                    </Grid>
                                </Grid>
                            </PremiumCard>

                            {selectedClass && selectedSubject && examType && (
                                <Box>
                                    {/* Stats Dashboard */}
                                    <Grid container spacing={3} sx={{ mb: 4 }}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <StatCard elevation={0}>
                                                <Avatar sx={{ bgcolor: '#e0f2fe', color: '#0ea5e9' }}><PeopleIcon /></Avatar>
                                                <Box>
                                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>STUDENTS</Typography>
                                                    <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.total}</Typography>
                                                </Box>
                                            </StatCard>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <StatCard elevation={0}>
                                                <Avatar sx={{ bgcolor: '#f0fdf4', color: '#10b981' }}><CheckCircleIcon /></Avatar>
                                                <Box>
                                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>PASS RATE</Typography>
                                                    <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.passRate}%</Typography>
                                                </Box>
                                            </StatCard>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <StatCard elevation={0}>
                                                <Avatar sx={{ bgcolor: '#fff7ed', color: '#f59e0b' }}><TrendingUpIcon /></Avatar>
                                                <Box>
                                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>AVG SCORE</Typography>
                                                    <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.average}</Typography>
                                                </Box>
                                            </StatCard>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <StatCard elevation={0}>
                                                <Avatar sx={{ bgcolor: '#eef2ff', color: '#6366f1' }}><PercentIcon /></Avatar>
                                                <Box sx={{ width: '100%' }}>
                                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>PROGRESS</Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.progress.toFixed(0)}%</Typography>
                                                        <LinearProgress variant="determinate" value={stats.progress} sx={{ flexGrow: 1, height: 8, borderRadius: 4, bgcolor: '#eef2ff', '& .MuiLinearProgress-bar': { bgcolor: '#6366f1' } }} />
                                                    </Box>
                                                </Box>
                                            </StatCard>
                                        </Grid>
                                    </Grid>

                                    {/* Action Bar */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <TextField 
                                                placeholder="Search student..." 
                                                size="small" 
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                                                sx={{ width: 220, bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                            />
                                            <TextField 
                                                label="Full Marks" 
                                                size="small" 
                                                type="number" 
                                                value={maxMarks} 
                                                onChange={(e) => setMaxMarks(e.target.value)}
                                                sx={{ width: 90, bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                            />
                                            <TextField 
                                                label="Pass Marks" 
                                                size="small" 
                                                type="number" 
                                                value={passMarks} 
                                                onChange={(e) => setPassMarks(e.target.value)}
                                                sx={{ width: 90, bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                            />
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <Button variant="outlined" onClick={() => { if(window.confirm("Fill empty with 0?")) { const m = {...studentMarks}; students.forEach(s => { if(!m[s._id]) m[s._id] = '0' }); setStudentMarks(m); } }} sx={{ borderRadius: '12px', fontWeight: 700 }}>Set Zero for Pending</Button>
                                            <Button variant="contained" startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />} onClick={handleSaveMarks} disabled={saving} sx={{ borderRadius: '12px', px: 4, bgcolor: '#6366f1', fontWeight: 800 }}>Save All</Button>
                                        </Box>
                                    </Box>

                                    <PremiumCard sx={{ border: 'none !important' }}>
                                        <TableContainer>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <StyledTableCell>Roll No</StyledTableCell>
                                                        <StyledTableCell>Student Name</StyledTableCell>
                                                        <StyledTableCell>Status</StyledTableCell>
                                                        <StyledTableCell align="right">Marks / {maxMarks}</StyledTableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {loading ? <TableRow><TableCell colSpan={4} align="center" sx={{ py: 5 }}><CircularProgress /></TableCell></TableRow> : filteredStudents.map((student) => {
                                                        const marks = studentMarks[student._id];
                                                        const isFail = marks !== '' && marks !== undefined && Number(marks) < Number(passMarks);
                                                        return (
                                                            <TableRow key={student._id} hover>
                                                                <TableCell sx={{ fontWeight: 700 }}>{student.rollNum}</TableCell>
                                                                <TableCell>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#6366f1', fontSize: '0.8rem' }}>{student.name.charAt(0)}</Avatar>
                                                                        <Typography sx={{ fontWeight: 600 }}>{student.name}</Typography>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell>
                                                                    {existingMarks[student._id] !== undefined ? <Chip label={isFail ? "Failed" : "Recorded"} size="small" color={isFail ? "error" : "success"} sx={{ fontWeight: 700 }} /> : marks ? <Chip label="Unsaved" size="small" color="warning" variant="outlined" sx={{ fontWeight: 700 }} /> : <Chip label="Pending" size="small" variant="outlined" sx={{ fontWeight: 700 }} />}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
                                                                        <TextField size="small" type="number" value={marks || ''} onChange={(e) => handleMarksChange(student._id, e.target.value)} sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: existingMarks[student._id] ? (isFail ? '#fef2f2' : '#f0fdf4') : 'white' } }} inputProps={{ style: { textAlign: 'right', fontWeight: 800, color: isFail ? '#ef4444' : 'inherit' } }} />
                                                                        <Typography variant="caption" sx={{ width: 40, fontWeight: 800, color: isFail ? '#ef4444' : '#94a3b8' }}>{marks ? `${((Number(marks)/Number(maxMarks))*100).toFixed(0)}%` : '0%'}</Typography>
                                                                    </Box>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </PremiumCard>
                                </Box>
                            )}
                        </Box>
                    </Fade>
                ) : (
                    <Fade in timeout={500}>
                        <Box sx={{ mt: 2 }}>
                            {historyLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
                            ) : sessions.length === 0 ? (
                                <Paper sx={{ p: 10, textAlign: 'center', borderRadius: '24px' }}>
                                    <HistoryIcon sx={{ fontSize: 60, color: '#e2e8f0', mb: 2 }} />
                                    <Typography variant="h5" color="textSecondary">No marks history found.</Typography>
                                    <Typography color="textSecondary">Start recording marks to see your history here.</Typography>
                                </Paper>
                            ) : (
                                <Grid container spacing={3}>
                                    {sessions.map((session) => (
                                        <Grid item xs={12} sm={6} md={4} key={session.key}>
                                            <SessionCard elevation={0}>
                                                <CardContent sx={{ p: 3 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                                        <Chip label={session.examType} size="small" sx={{ bgcolor: '#eef2ff', color: '#6366f1', fontWeight: 700 }} />
                                                        <Typography variant="caption" sx={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <CalendarIcon sx={{ fontSize: 14 }} /> {formatDatePart(session.examDate)}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: '#1e293b' }}>{session.subject}</Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                        <ClassIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                                                        <Typography sx={{ fontWeight: 600, color: '#475569' }}>Class {session.class}</Typography>
                                                    </Box>
                                                    <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
                                                    <Grid container spacing={1}>
                                                        <Grid item xs={6}>
                                                            <Typography variant="caption" color="textSecondary">STUDENTS</Typography>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{session.studentCount}</Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="caption" color="textSecondary">FULL MARKS</Typography>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{session.maxMarks}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                </CardContent>
                                                <CardActions sx={{ p: 2, pt: 0, mt: 'auto' }}>
                                                    <Button fullWidth variant="outlined" startIcon={<EditIcon />} onClick={() => handleEditSession(session)} sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}>Edit Data</Button>
                                                    <IconButton onClick={() => handleDeleteClick(session)} sx={{ color: '#ef4444', bgcolor: '#fef2f2', borderRadius: '12px' }}><DeleteIcon /></IconButton>
                                                </CardActions>
                                            </SessionCard>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    </Fade>
                )}
            </ContentContainer>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{
                    sx: { borderRadius: '24px', p: 1, maxWidth: '400px' }
                }}
            >
                <DialogTitle sx={{ fontWeight: 900, fontSize: '1.5rem', pb: 1 }}>
                    Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#475569', fontWeight: 500 }}>
                        Are you sure you want to delete all marks for 
                        <Box component="span" sx={{ color: '#1e293b', fontWeight: 800, display: 'block', mt: 1 }}>
                            {sessionToDelete?.class} - {sessionToDelete?.subject}
                        </Box>
                        on {sessionToDelete && formatDatePart(sessionToDelete.examDate)}?
                        <Box component="span" sx={{ color: '#ef4444', fontWeight: 700, display: 'block', mt: 2, fontSize: '0.875rem' }}>
                            ⚠️ This action cannot be undone.
                        </Box>
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button 
                        onClick={() => setDeleteDialogOpen(false)}
                        sx={{ borderRadius: '12px', fontWeight: 700, color: '#64748b', textTransform: 'none', px: 3 }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
                        sx={{ borderRadius: '12px', fontWeight: 800, textTransform: 'none', px: 3, bgcolor: '#ef4444' }}
                    >
                        {deleting ? 'Deleting...' : 'Delete All Marks'}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageWrapper>
    );
};

export default TeacherMarks;
